import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ListChecks, LayoutGrid, Globe2, Megaphone, Inbox,
  MessageSquare, Settings, LogOut, Menu, X, UserPlus, Mail,
} from "lucide-react";
import { api } from "../lib/api";
import Overview from "./Overview";
import Listings from "./Listings";
import Categories from "./Categories";
import Locations from "./Locations";
import Ads from "./Ads";
import AdRequests from "./AdRequests";
import Messages from "./Messages";
import Reviews from "./Reviews";
import Owners from "./Owners";
import SettingsPage from "./SettingsPage";

const TABS = [
  ["overview", "Overview", LayoutDashboard],
  ["listings", "Listings", ListChecks],
  ["owners", "Owners", UserPlus],
  ["categories", "Categories", LayoutGrid],
  ["locations", "Locations", Globe2],
  ["ads", "Ads & Campaigns", Megaphone],
  ["requests", "Ad Requests", Inbox],
  ["messages", "Messages", Mail],
  ["reviews", "Reviews", MessageSquare],
  ["settings", "Settings", Settings],
];

export default function AdminApp() {
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("overview");
  const [navOpen, setNavOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("bh_admin_token")) {
      navigate("/admin/login");
      return;
    }
    api.get("/auth/me")
      .then((r) => { setAdmin(r.data); setChecking(false); })
      .catch(() => {
        localStorage.removeItem("bh_admin_token");
        navigate("/admin/login");
      });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("bh_admin_token");
    navigate("/admin/login");
  };

  if (checking) {
    return <div className="min-h-screen bg-sand flex items-center justify-center"><div className="w-10 h-10 rounded-full border-2 border-gold border-t-transparent animate-spin" /></div>;
  }

  const content = {
    overview: <Overview setTab={setTab} />,
    listings: <Listings />,
    owners: <Owners />,
    categories: <Categories />,
    locations: <Locations />,
    ads: <Ads />,
    requests: <AdRequests />,
    messages: <Messages />,
    reviews: <Reviews />,
    settings: <SettingsPage />,
  }[tab];

  return (
    <div className="min-h-screen bg-sand/60 flex" data-testid="admin-dashboard">
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-teak text-cream border-r border-teak-surface flex flex-col transform transition-transform lg:translate-x-0 ${navOpen ? "translate-x-0" : "-translate-x-full"}`} data-testid="admin-sidebar">
        <a href="/" className="flex items-center gap-2.5 px-6 h-16 lg:h-20 border-b border-teak-surface hover:bg-teak-surface/50 transition-colors" data-testid="admin-brand-link">
          <img src="/bh-icon-180.png" alt="BurmeseHub logo" className="w-8 h-8 rounded-full" />
          <div>
            <p className="font-display font-extrabold text-sm leading-none">BURMESE <span className="text-gold">HUB</span></p>
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-cream/40 mt-1">Central Admin</p>
          </div>
        </a>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {TABS.map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => { setTab(key); setNavOpen(false); }}
              data-testid={`admin-tab-${key}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                tab === key ? "bg-ruby text-cream" : "text-cream/60 hover:text-cream hover:bg-teak-surface"
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-teak-surface">
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-cream/60 hover:text-cream hover:bg-teak-surface transition-colors" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="bg-teak-surface text-cream border-b border-teak-line px-4 sm:px-6 h-16 flex items-center justify-between" data-testid="admin-header">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2" onClick={() => setNavOpen(!navOpen)} data-testid="admin-nav-toggle" aria-label="Menu">
              {navOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <p className="font-display font-bold capitalize">{TABS.find(([k]) => k === tab)?.[1]}</p>
          </div>
          <p className="text-xs text-cream/50 font-mono">{admin?.email}</p>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">{content}</main>
      </div>
    </div>
  );
}
